import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ requires2FA: boolean; tempToken?: string }>;
  verify2FA: (token: string, tempToken: string) => Promise<void>;
  verifyBackup: (backupCode: string, tempToken: string) => Promise<void>;
  forgotPassword: (username: string) => Promise<{ resetUrl?: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => void;
  error: string | null;
  clearError: () => void;
  setNavigate: (navigate: (path: string) => void) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [error, setError] = useState<string | null>(null);
  const navigateRef = useRef<((path: string) => void) | null>(null);

  useEffect(() => {
    // Set authorization header immediately on mount if token exists
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
  }, []); // Run only on mount

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Set up axios response interceptor to handle unauthenticated responses
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if the error response indicates unauthenticated
        if (error.response?.data?.error === 'unauthenticated' || 
            error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes('unauthenticated')) {
          
          // Clear the token and redirect to login
          setToken(null);
          if (navigateRef.current) {
            navigateRef.current('/login');
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const setNavigate = (navigate: (path: string) => void) => {
    navigateRef.current = navigate;
  };

  const login = async (username: string, password: string) => {
    try {
      setError(null);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        username,
        password,
      });
      
      // Check if 2FA is required
      if (response.data.requires2FA) {
        return {
          requires2FA: true,
          tempToken: response.data.tempToken
        };
      } else {
        // Direct login successful
        setToken(response.data.token);
        return { requires2FA: false };
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Invalid email or password';
      setError(errorMessage);
      throw err;
    }
  };

  const verify2FA = async (token: string, tempToken: string) => {
    try {
      setError(null);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-2fa`, {
        token,
        tempToken,
      });
      setToken(response.data.token);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Invalid 2FA token';
      setError(errorMessage);
      throw err;
    }
  };

  const verifyBackup = async (backupCode: string, tempToken: string) => {
    try {
      setError(null);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-backup`, {
        backupCode,
        tempToken,
      });
      setToken(response.data.token);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Invalid backup code';
      setError(errorMessage);
      throw err;
    }
  };

  const forgotPassword = async (username: string) => {
    try {
      setError(null);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/forgot-password`, {
        username,
      });
      return { resetUrl: response.data.resetUrl };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to send password reset email';
      setError(errorMessage);
      throw err;
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      setError(null);
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        token,
        newPassword,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to reset password';
      setError(errorMessage);
      throw err;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setError(null);
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/change-password`, {
        currentPassword,
        newPassword,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to change password';
      setError(errorMessage);
      throw err;
    }
  };

  const logout = () => {
    setToken(null);
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated: !!token,
        login,
        verify2FA,
        verifyBackup,
        forgotPassword,
        resetPassword,
        changePassword,
        logout,
        error,
        clearError,
        setNavigate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 